import React, { useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import PrivacyClause from "@/components/legal/PrivacyClause";
import PrivacyCheckbox from "@/components/legal/PrivacyCheckbox";

const initial = { name: "", email: "", phone: "", message: "" };

/**
 * Formulario de contacto general en el Área de socios.
 * Para personas interesadas en colaborar con la asociación o pedir más
 * información. Se guarda como mensaje de contacto estándar.
 */
export default function RegistrationForm() {
    const [form, setForm] = useState(initial);
    const [privacyAccepted, setPrivacyAccepted] = useState(false);
    const [status, setStatus] = useState({ type: null, msg: "" });
    const [submitting, setSubmitting] = useState(false);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setStatus({ type: null, msg: "" });
        try {
            await api.post("/contact", {
                name: form.name,
                email: form.email,
                phone: form.phone,
                subject: "Contacto general · Área de socios",
                message: form.message,
            });
            setStatus({
                type: "success",
                msg: "Gracias, hemos recibido tu mensaje. Te responderemos lo antes posible.",
            });
            setForm(initial);
            setPrivacyAccepted(false);
        } catch (err) {
            setStatus({
                type: "error",
                msg: formatApiErrorDetail(err.response?.data?.detail) || err.message,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="registro" data-testid="contact-general-section" className="mt-16 scroll-mt-24">
            <div className="border-t border-[#E2E4E9] pt-16">
                <div className="max-w-2xl">
                    <div className="text-xs uppercase tracking-widest text-[#082050]/70 mb-4">
                        ¿Quieres colaborar con ASATTA?
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-semibold text-[#082050] tracking-tight">
                        Contáctanos.
                    </h2>
                    <p className="mt-4 text-[#4A5568] text-lg">
                        Escríbenos si eres alojamiento, entidad o profesional interesado
                        en colaborar con la asociación, o si necesitas más información.
                        Te responderemos en breve.
                    </p>
                </div>

                <form
                    onSubmit={onSubmit}
                    data-testid="general-contact-form"
                    className="mt-10 border border-[#E2E4E9] p-6 sm:p-10 space-y-5 bg-white"
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                            <label className="text-xs uppercase tracking-widest text-[#082050]/70">Nombre y apellidos</label>
                            <input
                                name="name"
                                required
                                value={form.name}
                                onChange={onChange}
                                data-testid="reg-name"
                                className="mt-2 w-full border border-[#E2E4E9] px-4 py-3 text-sm focus:outline-none focus:border-[#082050]"
                            />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-widest text-[#082050]/70">Email</label>
                            <input
                                type="email"
                                name="email"
                                required
                                value={form.email}
                                onChange={onChange}
                                data-testid="reg-email"
                                className="mt-2 w-full border border-[#E2E4E9] px-4 py-3 text-sm focus:outline-none focus:border-[#082050]"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#082050]/70">Teléfono</label>
                        <input
                            name="phone"
                            value={form.phone}
                            onChange={onChange}
                            data-testid="reg-phone"
                            className="mt-2 w-full border border-[#E2E4E9] px-4 py-3 text-sm focus:outline-none focus:border-[#082050]"
                        />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#082050]/70">Mensaje</label>
                        <textarea
                            name="message"
                            required
                            rows={5}
                            value={form.message}
                            onChange={onChange}
                            data-testid="reg-message"
                            className="mt-2 w-full border border-[#E2E4E9] px-4 py-3 text-sm focus:outline-none focus:border-[#082050] resize-y"
                        />
                    </div>

                    {status.msg && (
                        <div
                            data-testid={status.type === "success" ? "reg-success" : "reg-error"}
                            className={`text-sm border px-4 py-3 inline-flex items-start gap-2 ${
                                status.type === "success"
                                    ? "border-[#082050] text-[#082050] bg-[#082050]/5"
                                    : "border-red-500 text-red-600 bg-red-50"
                            }`}
                        >
                            {status.type === "success" && <CheckCircle2 size={16} className="mt-0.5 shrink-0" />}
                            <span>{status.msg}</span>
                        </div>
                    )}

                    <PrivacyClause variant="contact" />
                    <PrivacyCheckbox
                        checked={privacyAccepted}
                        onChange={setPrivacyAccepted}
                        testId="reg-privacy-checkbox"
                    />

                    <button
                        type="submit"
                        disabled={submitting || !privacyAccepted}
                        data-testid="btn-registration-submit"
                        className="bg-[#082050] text-white px-8 py-4 text-sm font-medium inline-flex items-center gap-2 hover:bg-[#082050]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-transform hover:-translate-y-0.5"
                    >
                        {submitting ? "Enviando..." : "Enviar mensaje"}
                        <ArrowRight size={14} />
                    </button>
                </form>
            </div>
        </section>
    );
}
